/**
 * Plan capabilities — aligned with src/config/plans.js (PLAN_FEATURE_ROWS).
 * Gating uses the user's billing plan from /api/users/me/usage only.
 */

export const PLAN_ORDER = ['FREE', 'PRO', 'TEAM'];

export const FEATURE = {
  LIBRARY: 'library',
  SAVE_PRACTICE_REVIEW: 'save_practice_review',
  LEARNING_DASHBOARD: 'learning_dashboard',
  ANALYTICS_BASIC: 'analytics_basic',
  ANALYTICS_ADVANCED: 'analytics_advanced',
  AI_EXPLAIN: 'ai_explain',
  LEARN_NOTEBOOKS: 'learn_notebooks',
  MIND_MAP_AI: 'mind_map_ai',
  ANALYTICS_EXPORT: 'analytics_export',
  NOTEBOOK_PRIVATE: 'notebook_private',
  NOTEBOOK_SHARE_TEAM: 'notebook_share_team',
  NOTEBOOK_INVITE_EMAIL: 'notebook_invite_email',
  TEAM_WORKSPACE: 'team_workspace',
  TEAM_CREATE: 'team_create',
  TEAM_LEADERBOARD_ANALYTICS: 'team_leaderboard_analytics',
  LEADERBOARD_RANK: 'leaderboard_rank',
};

export const FEATURE_MIN_PLAN = {
  [FEATURE.LIBRARY]: 'FREE',
  [FEATURE.SAVE_PRACTICE_REVIEW]: 'FREE',
  [FEATURE.LEARNING_DASHBOARD]: 'FREE',
  [FEATURE.ANALYTICS_BASIC]: 'FREE',
  [FEATURE.ANALYTICS_ADVANCED]: 'PRO',
  [FEATURE.AI_EXPLAIN]: 'FREE',
  [FEATURE.LEARN_NOTEBOOKS]: 'FREE',
  [FEATURE.MIND_MAP_AI]: 'PRO',
  [FEATURE.ANALYTICS_EXPORT]: 'PRO',
  [FEATURE.NOTEBOOK_PRIVATE]: 'PRO',
  [FEATURE.NOTEBOOK_SHARE_TEAM]: 'PRO',
  [FEATURE.NOTEBOOK_INVITE_EMAIL]: 'PRO',
  [FEATURE.TEAM_WORKSPACE]: 'TEAM',
  [FEATURE.TEAM_CREATE]: 'TEAM',
  [FEATURE.TEAM_LEADERBOARD_ANALYTICS]: 'TEAM',
  [FEATURE.LEADERBOARD_RANK]: 'PRO',
};

export const FEATURE_LABELS = {
  [FEATURE.ANALYTICS_BASIC]: 'Basic analytics',
  [FEATURE.ANALYTICS_ADVANCED]: 'Advanced analytics',
  [FEATURE.MIND_MAP_AI]: 'Mind map AI search',
  [FEATURE.ANALYTICS_EXPORT]: 'JSON export',
  [FEATURE.NOTEBOOK_PRIVATE]: 'Private notebooks',
  [FEATURE.NOTEBOOK_SHARE_TEAM]: 'Share notebooks with your team',
  [FEATURE.NOTEBOOK_INVITE_EMAIL]: 'Invite notebook collaborators by email',
  [FEATURE.TEAM_WORKSPACE]: 'Team workspace',
  [FEATURE.TEAM_CREATE]: 'Create a team organization',
  [FEATURE.TEAM_LEADERBOARD_ANALYTICS]: 'Team leaderboard & analytics',
  [FEATURE.LEADERBOARD_RANK]: 'Rank & leaderboard',
};

export function normalizePlan(plan) {
  const p = String(plan || 'FREE').trim().toUpperCase();
  return PLAN_ORDER.includes(p) ? p : 'FREE';
}

export function planRank(plan) {
  return PLAN_ORDER.indexOf(normalizePlan(plan));
}

export function planMeetsMinimum(userPlan, minPlan) {
  return planRank(userPlan) >= planRank(minPlan);
}

export function isProPlan(plan) {
  return normalizePlan(plan) === 'PRO';
}

export function isTeamPlan(plan) {
  return normalizePlan(plan) === 'TEAM';
}

export function isProOrTeamPlan(plan) {
  const p = normalizePlan(plan);
  return p === 'PRO' || p === 'TEAM';
}

/** Strict: billing plan only — no trial/org bypasses. */
export function canUseFeature(feature, ctx = {}) {
  const min = FEATURE_MIN_PLAN[feature] || 'FREE';
  const plan = normalizePlan(ctx.plan);
  return planMeetsMinimum(plan, min);
}

export function canAccessTeamWorkspace(ctx = {}) {
  return canUseFeature(FEATURE.TEAM_WORKSPACE, ctx);
}

export function canCreateTeamOrganization(ctx = {}) {
  return canUseFeature(FEATURE.TEAM_CREATE, ctx);
}

export function canUseTeamLeaderboardAnalytics(ctx = {}) {
  return canUseFeature(FEATURE.TEAM_LEADERBOARD_ANALYTICS, ctx);
}

export function canUseLeaderboardRank(ctx = {}) {
  return canUseFeature(FEATURE.LEADERBOARD_RANK, ctx);
}

export function canUseAdvancedAnalytics(ctx = {}) {
  return canUseFeature(FEATURE.ANALYTICS_ADVANCED, ctx);
}

export function canExportAnalytics(ctx = {}) {
  return canUseFeature(FEATURE.ANALYTICS_EXPORT, ctx);
}

/** Matches billing row: Basic / Advanced / Advanced + team */
export function analyticsTierLabel(plan) {
  const p = normalizePlan(plan);
  if (p === 'TEAM') return 'Advanced + team';
  if (p === 'PRO') return 'Advanced';
  return 'Basic';
}

export function analyticsAccessForPlan(plan) {
  const ctx = { plan };
  return {
    tierLabel: analyticsTierLabel(plan),
    basic: canUseFeature(FEATURE.ANALYTICS_BASIC, ctx),
    advanced: canUseAdvancedAnalytics(ctx),
    exportJson: canExportAnalytics(ctx),
    team: canUseTeamLeaderboardAnalytics(ctx),
  };
}

export function isTeamLeaderboardPath(pathname) {
  return /^\/teams\/[^/]+\/leaderboard\/?$/.test(pathname || '');
}

export function requiredPlanLabel(feature) {
  const min = FEATURE_MIN_PLAN[feature] || 'FREE';
  if (min === 'FREE') return 'Free';
  if (min === 'PRO') return 'Pro';
  return 'Team';
}

export function featureUpgradeHeadline(feature) {
  const label = FEATURE_LABELS[feature] || 'This feature';
  return `${label} requires ${requiredPlanLabel(feature)}`;
}

export function isTeamInviteAcceptPath(pathname) {
  return /^\/teams\/invite\//.test(pathname || '');
}

/** Route → required feature (null = no plan gate). */
export function resolveFeatureForPath(pathname) {
  if (!pathname || isTeamInviteAcceptPath(pathname)) return null;
  if (pathname === '/teams/new') return FEATURE.TEAM_CREATE;
  if (isTeamLeaderboardPath(pathname)) return FEATURE.TEAM_LEADERBOARD_ANALYTICS;
  if (pathname === '/leaderboard') return FEATURE.LEADERBOARD_RANK;
  if (pathname === '/teams' || pathname.startsWith('/teams/')) {
    return FEATURE.TEAM_WORKSPACE;
  }
  return null;
}
